import React, {useState} from 'react'
import {Box, Button, Link, SimpleGrid, Text, useMultiStyleConfig, Flex} from '@chakra-ui/react'
import {useForm} from 'react-hook-form'
import {FormattedMessage, useIntl} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useHistory} from 'react-router'
import {
    getInfoLabelVariantByOmsStatus,
    isEditOrCancelHidden,
    canEditOrCancelSubscription, // <-- import the new utility
} from '../../pages/account/my-orders/utils'
import PropTypes from 'prop-types'
import SubscriptionEditNotAllowedModal from '../../components/subscription-edit-modals/subscription-edit-not-allowed-modal'
import parse from 'html-react-parser'
import InformativeLabel from '../informative-label/partials/InformativeLabel'
import Field from '../field'

const SubscriptionsBox = ({subscription, detailPage = false}) => {
    const styles = useMultiStyleConfig('Subscriptions')
    const {locale, buildUrl} = useMultiSite()
    const localeId = locale.id.toLowerCase()
    const history = useHistory()
    const [selectedOrder, setSelectedOrder] = useState('')
    const {formatDate, formatMessage} = useIntl()
    const {control} = useForm({
        defaultValues: {
            selectedOrderId: '',
        },
    })
    const [notAllowedModal, setNotAllowedModal] = useState({isOpen: false, title: '', message: ''})

    // Navigate to subscription details page
    const handleSubscriptionClick = (subscriptionId) => {
        history.push(`/${localeId}/account/subscription-details/${subscriptionId}`)
    }

    // Calculate the subscription duration in months
    const periodTypeMap = {
        Monthly: 1,
        Bimonthly: 2,
        Quarterly: 3,
    }

    let subscriptionDuration = '-'
    try {
        const length = subscription?.renewal?.periodLength || subscription?.renewalPeriodLength
        const type = subscription?.renewal?.periodType || subscription?.renewalPeriodType
        const multiplier = periodTypeMap[type]
        if (typeof length === 'number' && typeof multiplier === 'number') {
            subscriptionDuration = length * multiplier
        }
    } catch (e) {
        subscriptionDuration = '-'
    }

    const handleSelectChange = (selectedOption) => {
        const selectedId = selectedOption?.value || ''
        const selected =
            subscription?.orderSummaries?.find((orderSummary) => orderSummary.id === selectedId) ||
            ''
        setSelectedOrder(selected)
    }

    // Handler for cancel button
    const handleCancelClick = () => {
        if (canEditOrCancelSubscription(subscription)) {
            history.push(`/${localeId}/account/subscription-cancel/${subscription?.id}`)
        } else {
            // Calculate the last editable date (2 days before renewal)
            let editableUntilStr = ''
            if (subscription?.renewal?.nextDate) {
                const nextDate = new Date(subscription.renewal.nextDate)
                const editableUntil = new Date(nextDate)
                editableUntil.setDate(editableUntil.getDate() - 2)
                editableUntilStr = editableUntil.toLocaleDateString()
            }
            const title = formatMessage({
                id: 'subscription_edit_not_allowed.cancel_title',
                defaultMessage: 'Cancel Subscription',
            })
            const message = formatMessage(
                {
                    id: 'subscription_edit_not_allowed.cancel_message',
                    defaultMessage:
                        'The subscription could be canceled until {date}. You will be able to cancel the subscription for the next order.',
                },
                {date: editableUntilStr},
            )
            setNotAllowedModal({isOpen: true, title, message})
        }
    }

    return (
        <Box {...styles.subscriptionBox} data-testid="subscription-box">
            {!detailPage && (
                <Text {...styles.subscriptionNumberHeader}>
                    <FormattedMessage
                        id="account_subscription_history.label.subscription_number"
                        defaultMessage="Subscription number: {subscriptionNumber}"
                        values={{
                            subscriptionNumber: subscription?.name,
                        }}
                    />
                </Text>
            )}
            <Box {...styles.subscriptionBoxContainer}>
                <SimpleGrid {...styles.subscriptionBoxUpContainer}>
                    <Box {...styles.subscriptionInnerBoxContainer}>
                        <Text {...styles.subscriptionBoxLabel}>
                            <FormattedMessage
                                id="account_subscription_history.data.content"
                                defaultMessage="Content"
                            />
                        </Text>
                        <Box
                            listStyleType="none"
                            listStylePosition="inside"
                            sx={{
                                '& li': {
                                    position: 'relative',
                                    paddingLeft: '18px',
                                },
                                '& li::before': {
                                    content: '"•"',
                                    color: 'black',
                                    fontSize: '1em',
                                    position: 'absolute',
                                    left: '0',
                                    top: '0',
                                },
                            }}
                            as="ul"
                        >
                            {parse(
                                // Remove wrapping <ul> if present, keep only <li> elements
                                (subscription?.subscriptionCommercialName || '').replace(
                                    /<\/?ul[^>]*>/g,
                                    '',
                                ),
                            )}
                        </Box>
                    </Box>

                    <Box {...styles.subscriptionInnerBoxContainer}>
                        <Text {...styles.subscriptionBoxLabel}>
                            <FormattedMessage
                                id="account_subscription_history.data.subscription_date"
                                defaultMessage="Subscription date"
                            />
                        </Text>
                        <Text>{formatDate(subscription?.subscriptionStartDate)}</Text>
                    </Box>
                    <Box {...styles.subscriptionInnerBoxContainer}>
                        <Text {...styles.subscriptionBoxLabel}>
                            <FormattedMessage
                                id="account_subscription_history.data.subscription_duration"
                                defaultMessage="Subscription duration"
                            />
                        </Text>
                        <Text>
                            <FormattedMessage
                                id="account_subscription_history.data.subscription_duration.value"
                                defaultMessage="{duration} months"
                                values={{
                                    duration: subscriptionDuration,
                                }}
                            />
                        </Text>
                    </Box>
                    <Box {...styles.subscriptionStatusDetailsContainer}>
                        <Box {...styles.subscriptionStatusContainer}>
                            <Text {...styles.subscriptionBoxLabel}>
                                <FormattedMessage
                                    id="account_order_history.label.status"
                                    defaultMessage="Status"
                                />
                            </Text>
                            <Flex {...styles.statusLabel}>
                                <InformativeLabel
                                    label={subscription?.status}
                                    width="fit-content"
                                    variant={getInfoLabelVariantByOmsStatus(
                                        subscription?.status?.toLowerCase(),
                                    )}
                                />

                                {/* Cancel button on the right in detailPage */}
                                {detailPage && !isEditOrCancelHidden(subscription?.status) && (
                                    <Button
                                        variant="link-button"
                                        size="base"
                                        ml={4}
                                        onClick={handleCancelClick}
                                    >
                                        <FormattedMessage
                                            id="form_action_buttons.button.cancel"
                                            defaultMessage="Cancel"
                                        />
                                    </Button>
                                )}
                            </Flex>
                        </Box>
                        {!detailPage && (
                            <Box {...styles.subscriptionDetailsLabelContainer}>
                                <Button
                                    as="a"
                                    variant="link-button"
                                    size="base"
                                    cursor="pointer"
                                    onClick={() => handleSubscriptionClick(subscription?.id)}
                                >
                                    <FormattedMessage
                                        id="account_order_history.link.details"
                                        defaultMessage="Details"
                                    />
                                </Button>
                            </Box>
                        )}
                    </Box>
                </SimpleGrid>
                {detailPage && (
                    <Box {...styles.subscriptionBoxBottomContainer}>
                        <Text {...styles.subscriptionBoxLabel}>
                            <FormattedMessage
                                id="account_subscription_history.data.shipped_orders"
                                defaultMessage="Shipped orders total: {ordersTotal}"
                                values={{
                                    ordersTotal: subscription?.orderSummaries?.length,
                                }}
                            />
                        </Text>
                        {subscription?.status === 'Active' && (
                            <Text>
                                <FormattedMessage
                                    id="account_subscription_history.data.subscription_duration"
                                    defaultMessage="Next order execution date: {date}"
                                    values={{
                                        date: formatDate(subscription?.renewal?.nextDate),
                                    }}
                                />
                            </Text>
                        )}

                        <Box {...styles.selectOrderContainer}>
                            <Field
                                name="selectedOrderId"
                                type="select"
                                control={control}
                                placeholder={formatMessage({
                                    id: 'account_subscription_history.data.select_order',
                                    defaultMessage: 'Select order',
                                })}
                                options={
                                    subscription?.orderSummaries?.map((opt) => ({
                                        label: opt.orderNumber,
                                        value: opt.id,
                                    })) || []
                                }
                                inputProps={({onChange}) => ({
                                    onChange: (selectedOption) => {
                                        onChange(selectedOption)
                                        handleSelectChange(selectedOption)
                                    },
                                })}
                            />
                            <Button
                                as={Link}
                                href={
                                    selectedOrder
                                        ? buildUrl(
                                              `/account/orders/${selectedOrder.id}/${selectedOrder.orderNumber}/${selectedOrder.originalOrderId}`,
                                          )
                                        : '#'
                                }
                                variant="primary"
                                size="large"
                                isDisabled={!selectedOrder}
                                {...styles.selectOrderButton}
                            >
                                <Text {...styles.selectOrderButtonText}>
                                    <FormattedMessage
                                        id="account_subscription_history.data.button"
                                        defaultMessage="Go to order detail"
                                    />
                                </Text>
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
            <SubscriptionEditNotAllowedModal
                isOpen={notAllowedModal.isOpen}
                onClose={() => setNotAllowedModal({...notAllowedModal, isOpen: false})}
                title={notAllowedModal.title}
                message={notAllowedModal.message}
            />
        </Box>
    )
}

SubscriptionsBox.propTypes = {
    subscription: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        status: PropTypes.string,
        subscriptionStartDate: PropTypes.string,
        subscriptionCommercialName: PropTypes.string,
        renewalPeriodLength: PropTypes.number,
        renewalPeriodType: PropTypes.string,
        subscriptionItems: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
            }),
        ),
        orderSummaries: PropTypes.array,
        renewal: PropTypes.object,
    }),
    detailPage: PropTypes.bool,
}

export default SubscriptionsBox
